/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, Modal, Select, Tab } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import useMounted from '../../shared/useMount/useMount';
import './ScopeOneErrorModel.scss';
import descriptors from '../../../locales/en-US/descriptors';
import { getTabsScopeOneError } from '../Utils/CommonWidgetUtils';
import {
  dataAvailableCondition,
  getConnectorConfigScopeOneError,
  getYearValues,
  isDataAvailableCheck
} from '../../ManualUpload/Scope1Table/Scope1TableUtils';
import useConnectorAndDynamicProps from '../useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { IUploadFile } from '../../ManualUpload/DataUploadSettings/DataUploadFileTable';
import withLoadingStatus from '../StatusWrappers/LoadingStatus';
import withErrorStatus from '../StatusWrappers/ErrorStatus';
import Scope1Table, {
  ScopeOneData
} from '../../ManualUpload/Scope1Table/Scope1Table';
import ConnectionError from '../ErrorComponents/ConnectionError';
import {
  apiNoContentStatusCode,
  MAGIC_VALUE,
  parseIntBase
} from '../Utils/Constants';
import { dataAvailableCheck, isObjectOrArrayAndEmpty } from '../Utils/Utils';
import { useFilterScope1Context } from '../../../context/FilterScope1Context/FilterScope1Context';
import { getNotifyTypeForAddFuel } from '../../ManualUpload/AddFuelModal/AddFuelUtils';
import useConnectorForPost from '../useConnectorAndDynamicProps/useConnectorForPost';
import { ApiContext } from '../../../context/ApiContext';
interface IDataUploadContent {
  data: IUploadFile[] | ScopeOneData[];
  onRefresh: () => Promise<void>;
  statusCode: number | null;
  activeIndex: number;
  SetHideFooter: IAny;
  selectedYear: string;
  setSelectedYear: Function;
}
const DataUploadContent = (props: IDataUploadContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    data,
    onRefresh,
    statusCode,
    SetHideFooter,
    selectedYear,
    setSelectedYear
  } = props;

  if (
    isObjectOrArrayAndEmpty(data) &&
    statusCode &&
    !(statusCode == apiNoContentStatusCode)
  ) {
    return (
      <ConnectionError
        title={t(d.CONNECTIONERRORTITLE)}
        message={t(d.CONNECTIONERROR)}
        onRefresh={onRefresh}
      />
    );
  }
  return (
    <div>
      <div className="yearSelectionScopeOneMissingData">
        <Select
          defaultValue={selectedYear}
          options={getYearValues()}
          onChange={(value: React.SetStateAction<string>) =>
            setSelectedYear(value)
          }
        />
      </div>
      <Scope1Table
        data={data as ScopeOneData[]}
        scopeDetails={'ScopeError'}
        SetHideFooter={SetHideFooter}
        selectedYear={selectedYear}
      />
    </div>
  );
};
const DataUploadComponentWithLoader = withLoadingStatus(
  withErrorStatus(DataUploadContent)
);
const ScopeOneErrorModel = (props: IAny) => {
  const [activeIndex, SetactiveIndex] = useState(0);
  const [hideFooter, SetHideFooter] = useState(true);
  const [state] = useFilterScope1Context();
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const apiContext = useContext(ApiContext);
  const { notifyMessage } = apiContext;
  const {
    showErrorModal,
    bearerToken,
    requestContext,
    setpopUpDisplay,
    selectedFuelMonthData,
    modalType,
    isEditButtonEnabled
  } = props.modalVal;
  const { saveErrorData } = state;
  const d = descriptors.energyManagement;
  const { toggleFeatureScopeOne } = props;
  const isMounted = useMounted();
  const connectorConfig = getConnectorConfigScopeOneError(
    activeIndex,
    selectedYear
  ) as IAny;
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const connectorConfigUpdate = {
    name: 'update-missing-data-details'
  };
  const dataConnectorUpdate = useConnectorForPost({
    connectorConfig: connectorConfigUpdate,
    requestContext
  });

  useEffect(() => {
    if (isMounted && showErrorModal === true) {
      dataConnector?.actions?.getAll();
    }
  }, [activeIndex, requestContext, showErrorModal]);

  useEffect(() => {
    dataConnector?.actions?.getAll();
  }, [selectedYear]);

  const onRefresh = () => dataConnector?.actions?.getAll();
  const removeSilentLoadingonRefresh = () => {
    return onRefresh();
  };
  const { all, loading, error, status } = dataConnector.data;

  const fileRecords = all as IAny;
  const isDataAvailable = isDataAvailableCheck(fileRecords);
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState<IUploadFile[]>([]);

  useEffect(() => {
    setIsLoading(loading);
    dataAvailableCondition(
      isDataAvailable,
      error,
      status,
      loading,
      fileRecords,
      setData,
      setErrorInfo
    );
    setStatusCode(status);
  }, [isDataAvailable, loading, status]);

  const [t] = useTranslation();

  const getCommonHOCForDataUpload = () => {
    return (
      <Card.Content className="data-upload-content-container">
        <DataUploadComponentWithLoader
          loading={isLoading}
          loadingText={t(d.LOADING)}
          data={data}
          hideNoData={true}
          onRefresh={removeSilentLoadingonRefresh}
          title={t(d.CONNECTIONERRORTITLE)}
          connectionErrorMessage={t(d.CONNECTIONERROR)}
          statusCode={statusCode}
          silentLoading={false}
          error={errorInfo}
          noDataMessage={''}
          activeIndex={activeIndex}
          SetHideFooter={SetHideFooter}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
        />{' '}
      </Card.Content>
    );
  };

  const renderModel = () => {
    return (
      <Tab
        defaultActiveIndex={activeIndex}
        onTabChange={(index) => {
          setData([]);
          SetactiveIndex(index);
          SetHideFooter(true);
          setSelectedYear(new Date().getFullYear().toString());
        }}
      >
        {getTabsScopeOneError(
          t,
          true,
          true,
          getCommonHOCForDataUpload,
          true
        ).map((tab) => tab)}
      </Tab>
    );
  };

  const closeErrorModal = () => {
    SetHideFooter(true);
    setpopUpDisplay(false);
  };
  function flatten(ary: any[], ret = []) {
    return ary.reduce((ret, entry) => {
      if (Array.isArray(entry)) {
        flatten(entry, ret);
      } else {
        ret.push(entry);
      }
      return ret;
    }, ret);
  }
  const saveData = async () => {
    let payload = saveErrorData
      .map(
        (val: {
          [x: string]: {
            [x: string]: any;
            keyName: string;
            isPopupOpen: boolean;
            changedMonthVal: boolean;
          };
        }) => {
          let arrayValue: any[] = [];
          Object.entries(val).forEach(([key, value]) => {
            if (val[key]?.changedMonthVal === true) {
              value.keyName = key;
              let newVal = {
                quantity: parseFloat(value.quantity),
                month: value.keyName.substring(0, MAGIC_VALUE.THREE),
                year: parseInt(value.year, parseIntBase)
              };
              arrayValue.push(newVal);
            }
          });
          return {
            emissionFactorId: val.emissionFactorId,
            fuelConsumpId: val.fuelConsumpId,
            fuelName: val.fuelName,
            fuelTypeId: val.fuleTypeId,
            emissionFactor: val.Co2,
            selectedYear: val.selectedYear,
            unit: val.unit,
            consumptionDetails: flatten(arrayValue).filter(
              (x: any) => typeof x === 'object'
            )
          };
        }
      )
      .filter(
        (removeEmptyconsumptionDetails: IAny) =>
          removeEmptyconsumptionDetails?.consumptionDetails?.length > 0
      );
    const result = await dataConnectorUpdate.actions.create(payload);
    SetHideFooter(true);
    setpopUpDisplay(false);
  };

  return (
    <div className="error-popup">
      <Modal
        className="emaas-assign-error-popup"
        size="small"
        open={showErrorModal}
        closeOnDimmerClick={false}
        closeIcon={true}
      >
        <Modal.Header className="emaas-add-Scope1Error-header">
          <span className="headerNameScope1">{t(d.SCOPE1DATAMISSING)}</span>
          <span className="SubheaderName">{t(d.SCOPE1SHOWMISSINGDATA)}</span>
        </Modal.Header>
        <Modal.Content>
          <div className="emaas-add-mainmeter-container">{renderModel()}</div>
          <div className="footer-scopeOne-error">
            {hideFooter ? (
              <Button
                className="applyFilterButton"
                type="primary"
                size="small"
                content={t(d.CLOSE)}
                onClick={() => closeErrorModal()}
              />
            ) : (
              <>
                <Button
                  className="resetFilterButton"
                  type="link"
                  size="small"
                  content={t(d.CANCEL)}
                  onClick={() => closeErrorModal()}
                />
                <Button
                  className="applyFilterButton"
                  type="primary"
                  size="small"
                  content={t(d.APPLY)}
                  onClick={saveData}
                />
              </>
            )}
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default ScopeOneErrorModel;
